(function () {
  const SELECTOR = '[data-video-testimonials]';
  const instances = new WeakSet();

  function init(root) {
    if (!root || instances.has(root)) return;
    instances.add(root);

    const section = root.closest('.video-testimonials');
    const track = root.querySelector('[data-videos-track]');
    const viewport = root.querySelector('[data-videos-viewport]');
    const prevBtn = section ? section.querySelector('[data-videos-prev]') : null;
    const nextBtn = section ? section.querySelector('[data-videos-next]') : null;
    if (!track || !viewport) return;

    const originalCards = Array.from(track.querySelectorAll('[data-videos-card]'));
    if (originalCards.length === 0) return;
    const n = originalCards.length;

    // --- Clone for seamless infinite loop ---
    // Layout becomes: [pre clones (n)] [originals (n)] [post clones (n)]
    // active index moves linearly; when it drifts outside [n, 2n-1] we silently
    // snap it back by ±n after the transition finishes — the clones at the
    // jump point hold identical content so the user sees no jump.
    if (n >= 2) {
      const ref = originalCards[0];
      originalCards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-videos-clone', 'pre');
        clone.setAttribute('aria-hidden', 'true');
        track.insertBefore(clone, ref);
      });
      originalCards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-videos-clone', 'post');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }

    const cards = Array.from(track.querySelectorAll('[data-videos-card]'));
    const hasClones = cards.length > n;

    const gap = Number.parseFloat(root.dataset.cardGap || '24') || 0;

    const initialOriginal = (() => {
      const raw = Number.parseInt(root.dataset.initialActive || '0', 10);
      if (Number.isNaN(raw) || raw < 0 || raw >= n) return 0;
      return raw;
    })();

    // When cloning, start in the middle set so we have equal room on both sides.
    let activeIndex = hasClones ? n + initialOriginal : initialOriginal;

    const signedCalc = (v) => (v >= 0 ? `+ ${v}px` : `- ${-v}px`);

    const apply = () => {
      const cardWidth = cards[0] ? cards[0].offsetWidth : 820;

      cards.forEach((card, i) => {
        const delta = i - activeIndex;
        const absDelta = Math.abs(delta);

        card.classList.toggle('is-active', delta === 0);
        card.classList.toggle('is-peek', absDelta === 1);
        card.classList.toggle('is-hidden', absDelta > 1);
        card.setAttribute('aria-hidden', delta === 0 ? 'false' : 'true');

        card.style.transformOrigin = '50% 50%';

        if (delta === 0) {
          card.style.transform = 'translate3d(-50%, -50%, 0)';
          return;
        }

        const direction = Math.sign(delta);
        const xOffset = direction * (cardWidth + gap) * absDelta;
        const xStr = signedCalc(xOffset);
        card.style.transform = `translate3d(calc(-50% ${xStr}), -50%, 0)`;
      });
    };

    let snapTimer = null;
    const TRANSITION_MS = 600;

    const snapIfNeeded = () => {
      if (!hasClones) return;
      let shift = 0;
      if (activeIndex < n) shift = n;
      else if (activeIndex >= 2 * n) shift = -n;
      if (shift === 0) return;
      activeIndex += shift;
      // Disable transitions during the silent jump, then restore on next frame.
      const prevTransitions = cards.map((c) => c.style.transition);
      cards.forEach((c) => {
        c.style.transition = 'none';
      });
      apply();
      // Force reflow so the browser commits the no-transition state.
      // eslint-disable-next-line no-unused-expressions
      track.offsetHeight;
      requestAnimationFrame(() => {
        cards.forEach((c, i) => {
          c.style.transition = prevTransitions[i] || '';
        });
      });
    };

    const scheduleSnap = () => {
      if (snapTimer) clearTimeout(snapTimer);
      snapTimer = setTimeout(snapIfNeeded, TRANSITION_MS + 50);
    };

    const setActive = (i) => {
      if (typeof stopAllVideos === 'function') stopAllVideos();
      if (hasClones) {
        activeIndex = i;
      } else {
        activeIndex = ((i % n) + n) % n;
      }
      apply();
      scheduleSnap();
    };

    if (prevBtn) prevBtn.addEventListener('click', () => setActive(activeIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => setActive(activeIndex + 1));

    viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActive(activeIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActive(activeIndex + 1);
      }
    });

    // Touch swipe
    let touchStartX = null;
    let touchStartY = null;
    viewport.addEventListener(
      'touchstart',
      (e) => {
        const t = e.changedTouches[0];
        touchStartX = t.clientX;
        touchStartY = t.clientY;
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchend',
      (e) => {
        if (touchStartX === null) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchStartX;
        const dy = t.clientY - touchStartY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) setActive(activeIndex + 1);
          else setActive(activeIndex - 1);
        }
        touchStartX = null;
        touchStartY = null;
      },
      { passive: true }
    );

    // Video play/pause
    const stopAllVideos = () => {
      cards.forEach((c) => {
        const v = c.querySelector('[data-testimonial-video]');
        if (v) {
          v.pause();
          v.muted = true;
          v.removeAttribute('controls');
          v.currentTime = 0.01;
        }
        c.dataset.playing = 'false';
      });
    };

    const togglePlay = (card) => {
      const video = card.querySelector('[data-testimonial-video]');
      if (!video) return;

      if (card.dataset.playing === 'true') {
        stopAllVideos();
        return;
      }

      stopAllVideos();
      video.setAttribute('controls', '');
      video.muted = false;
      card.dataset.playing = 'true';

      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          video.muted = true;
          const retry = video.play();
          if (retry && typeof retry.catch === 'function') {
            retry.catch(() => { card.dataset.playing = 'false'; });
          }
        });
      }
    };

    cards.forEach((card, i) => {
      card.addEventListener('click', (e) => {
        if (card.classList.contains('is-active')) {
          if (e.target.closest('video[controls]')) return;
          e.preventDefault();
          togglePlay(card);
        } else if (card.classList.contains('is-peek')) {
          setActive(i);
        }
      });
    });

    const place = () => apply();
    if (document.readyState === 'complete') {
      place();
    } else {
      window.addEventListener('load', place, { once: true });
      requestAnimationFrame(place);
    }

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(apply, 120);
    });
  }

  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const el = e.target && e.target.querySelector ? e.target.querySelector(SELECTOR) : null;
    if (el) init(el);
  });
})();
